import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';

const ApartmentsPage = () => {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ apartmentName: '', location: '', address: '', totalUnits: '' });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const fetch = useCallback(async () => {
    try {
      const { data } = await api.get('/apartments');
      setApartments(data.data);
    } catch { toast('Failed to load apartments', 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetch(); }, [fetch]);

  const openAdd = () => { setEditing(null); setForm({ apartmentName: '', location: '', address: '', totalUnits: '' }); setShowModal(true); };
  const openEdit = (apt) => { setEditing(apt); setForm({ apartmentName: apt.apartment_name, location: apt.location || '', address: apt.address || '', totalUnits: apt.total_units || '' }); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.apartmentName) { toast('Apartment name is required', 'error'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/apartments/${editing.id}`, form);
        toast('Apartment updated!', 'success');
      } else {
        await api.post('/apartments', form);
        toast('Apartment created!', 'success');
      }
      setShowModal(false);
      fetch();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this apartment?')) return;
    try {
      await api.delete(`/apartments/${id}`);
      toast('Apartment deactivated', 'success');
      fetch();
    } catch { toast('Failed to delete', 'error'); }
  };

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;

  return (
    <div>
      <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>🏢 Delivery Apartments</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{apartments.length} apartments registered</p>
        </div>
        <button id="add-apt-btn" className="btn btn-primary" onClick={openAdd}>+ Add Apartment</button>
      </div>

      {apartments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏢</div>
          <p>No apartments yet. Add your first one!</p>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Apartment</button>
        </div>
      ) : (
        <div className="apt-grid">
          {apartments.map(apt => (
            <div key={apt.id} className="apt-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div className="apt-name">{apt.apartment_name}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm btn-icon" onClick={e => { e.stopPropagation(); openEdit(apt); }} title="Edit">✏️</button>
                  <button className="btn btn-danger btn-sm btn-icon" onClick={e => { e.stopPropagation(); handleDelete(apt.id); }} title="Delete">🗑️</button>
                </div>
              </div>
              <div className="apt-location">📍 {apt.location || apt.address || 'No location set'}</div>
              <div className="apt-stats">
                <div className="apt-stat">
                  <div className="apt-stat-value">{apt.total_units || 0}</div>
                  <div className="apt-stat-label">Units</div>
                </div>
                <div className="apt-stat">
                  <div className="apt-stat-value" style={{ color: apt.pending_orders > 0 ? 'var(--warning)' : 'var(--success)' }}>
                    {apt.pending_orders || 0}
                  </div>
                  <div className="apt-stat-label">Active Orders</div>
                </div>
              </div>
              <button
                className="btn btn-primary btn-sm"
                style={{ width: '100%', marginTop: 14, justifyContent: 'center' }}
                onClick={() => navigate(`/apartments/${apt.id}`)}
              >
                View Orders →
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editing ? 'Edit Apartment' : 'Add Apartment'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} className="form-grid">
              <div className="form-group">
                <label className="form-label">Apartment Name *</label>
                <input id="apt-name" className="form-input" placeholder="e.g. Green Valley Towers" value={form.apartmentName} onChange={e => setForm(p => ({ ...p, apartmentName: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input className="form-input" placeholder="e.g. Sector 14, Chennai" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Full Address</label>
                <textarea className="form-textarea" placeholder="Full address..." value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Total Units</label>
                <input className="form-input" type="number" placeholder="e.g. 120" value={form.totalUnits} onChange={e => setForm(p => ({ ...p, totalUnits: e.target.value }))} />
              </div>
              <div className="modal-footer" style={{ margin: 0, padding: 0, border: 'none', marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApartmentsPage;
